import { useState, useCallback } from 'react';

const initialSnackbarState = {
    open: false,
    message: '',
    severity: 'info', // 'error', 'warning', 'info', 'success'
};

const initialPopupState = {
    open: false,
    type: '', // "cancel" | "delete" | "success" | "error"
    message: '',
    onConfirm: null,
};

/**
 * A centralized hook for managing Snackbar and Dialog popups.
 */
export const useAlerts = () => {
    const [snackbar, setSnackbar] = useState(initialSnackbarState);
    const [popup, setPopup] = useState(initialPopupState);

    const hideSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    const hidePopup = useCallback(() => {
        setPopup(prev => ({ ...prev, open: false, onConfirm: null }));
    }, []);

    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const showErrorSnackbar = useCallback((message = 'An error occurred.') => {
        showSnackbar(message, 'error');
    }, [showSnackbar]);

    const showSuccessSnackbar = useCallback((message = 'Operation successful.') => {
        showSnackbar(message, 'success');
    }, [showSnackbar]);

    const showPopup = useCallback((config) => {
        setPopup({ open: true, ...config });
    }, []);
    
    const showSuccessPopup = useCallback((message, onConfirm) => {
        showPopup({
            type: 'success',
            message,
            onConfirm: () => {
                hidePopup();
                if (onConfirm) onConfirm();
            }
        });
    }, [showPopup, hidePopup]);

    const showErrorPopup = useCallback((message, onConfirm) => {
        showPopup({
            type: 'error',
            message,
            onConfirm: () => {
                hidePopup();
                if (onConfirm) onConfirm();
            }
        });
    }, [showPopup, hidePopup]);

    const showDeleteConfirm = useCallback((message, onConfirm) => {
        showPopup({
            type: 'delete',
            message,
            onConfirm: () => {
                hidePopup();
                if (onConfirm) onConfirm();
            }
        });
    }, [showPopup, hidePopup]);
    
    const showCancelConfirm = useCallback((message, onConfirm) => {
        showPopup({
            type: 'cancel',
            message,
            onConfirm: () => {
                hidePopup();
                if (onConfirm) onConfirm();
            }
        });
    }, [showPopup, hidePopup]);

    return {
        snackbar,
        popup,
        hideSnackbar,
        hidePopup,
        showSnackbar,
        showErrorSnackbar,
        showSuccessSnackbar,
        showPopup,
        showSuccessPopup,
        showErrorPopup,
        showDeleteConfirm,
        showCancelConfirm,
    };
};
